import { useMemo } from 'react'

import {
  matchFilterArrayIncludes, matchFilterNumber, matchFilterString, sortArrayBy,
} from '@domain-group/javascript-utils'
import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { EntitiesState } from '@redux/entities'
import type { ProviderPriceListModel } from '@models/types'

const watchEntityKeys = ['providerPriceLists']

type MatchPublicationIdParams = {
  filterPublicationId?: number,
  providerPriceList: ProviderPriceListModel,
  publications: EntitiesState['publications'],
}

const matchPublicationPriceListId = (params: MatchPublicationIdParams) => {
  const { filterPublicationId, providerPriceList, publications } = params

  if (filterPublicationId && publications[filterPublicationId]){
    const publication = publications[filterPublicationId]
    return publication.provider_price_list_id === providerPriceList.id
  }

  return true
}

type ProviderPriceListsFilters = {
  agency_id?: number,
  hidden?: boolean,
  is_master?: boolean,
  master_price_list_id?: number,
  provider_id?: number,
  publication_id?: number,
  service_id?: number,
  service_product_id?: number,
  start_date?: string,
  string?: string,
}

interface Options {
  entityKey?: string,
  filters?: ProviderPriceListsFilters,
  performHttpRequests?: boolean,
  requestOptions?: object,
}

function useProviderPriceLists(options: Options = {}) {
  const { entityKey, filters, requestOptions = {} } = options
  const {
    agency_id: filterAgencyId,
    hidden: filterHidden,
    is_master: filterIsMaster,
    master_price_list_id: filterMasterPriceListId,
    provider_id: filterProviderId,
    publication_id: filterPublicationId,
    service_id: filterServiceId,
    service_product_id: filterServiceProductId,
    string: filterString,
  } = filters || {}

  const {
    updatedEntities: {
      providerPriceLists: providerPriceListsUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const entities = useSelector(reduxState => reduxState.entities)
  const { providerPriceLists, publications } = entities

  const filteredProviderPriceLists = useMemo(() => {
    const filtered = Object.values(providerPriceLists).filter((providerPriceList) => {
      const {
        assigned_service_product_ids = [],
        hidden,
        id,
        is_master,
        master_price_list_id,
        provider_id,
        service_id,
        title,
      } = providerPriceList

      const matchId = id.toString() === filterString
      const matchHidden = typeof filterHidden !== 'undefined' ? hidden === filterHidden : true
      const matchIsMaster = typeof filterIsMaster !== 'undefined' ? is_master === filterIsMaster : true
      const matchMasterPriceListId = matchFilterNumber(master_price_list_id, filterMasterPriceListId)
      const matchProviderId = matchFilterNumber(provider_id, filterProviderId)
      const matchPublicationId = matchPublicationPriceListId({
        filterPublicationId,
        providerPriceList,
        publications,
      })
      const matchServiceId = matchFilterNumber(service_id, filterServiceId)
      const matchServiceProduct = matchFilterArrayIncludes(assigned_service_product_ids, filterServiceProductId)
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle

      return matchHidden
        && matchIsMaster
        && matchMasterPriceListId
        && matchProviderId
        && matchPublicationId
        && matchServiceId
        && matchServiceProduct
        && matchString
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [providerPriceListsUpdatedAt, JSON.stringify(filters)])

  const filteredProviderPriceListsCount = filteredProviderPriceLists.length
  const hasFilteredProviderPriceLists = !!filteredProviderPriceListsCount

  const loadMorePayload = useLoadMore({
    loadedCount: filteredProviderPriceListsCount,
    filters: {
      ...filters,
    },
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingProviderPriceLists } = useReduxAction(
    'providerPriceLists',
    'loadProviderPriceLists',
    {
      entityKey,
      limit,
      ...requestOptions,
      ...filtersWithOffset,
    },
    [performHttpRequests, filtersWithOffset],
    {
      shouldPerformFn: () => performHttpRequests,
    },
  )

  return {
    callbacks: { loadMore },
    canLoadMore,
    filteredProviderPriceLists,
    filteredProviderPriceListsCount,
    hasFilteredProviderPriceLists,
    loading: loadingProviderPriceLists,
  }
}

export default useProviderPriceLists
